import React, { useEffect, useMemo } from 'react';
import { Route, Switch, useParams, useHistory, useLocation } from 'react-router-dom';
import { FiCountry, FiLanguage, useLocale } from '../contexts/LocaleContext';

// Build route pattern dynamically from enums
const COUNTRY_PATTERN = Object.values(FiCountry)
  .map((c) => c.toLowerCase())
  .join('|');
const LOCALE_PATTERN = Object.values(FiLanguage)
  .map((l) => l.toLowerCase())
  .join('|');
const LOCALE_ROUTE_PATH = `/:country(${COUNTRY_PATTERN})/:locale(${LOCALE_PATTERN})`;

// Pre-compute lowercased sets for suffix detection
const VALID_COUNTRIES_LOWER = Object.values(FiCountry).map((c) => c.toLowerCase());
const VALID_LOCALES_LOWER = Object.values(FiLanguage).map((l) => l.toLowerCase());

/**
 * Wrapper that handles locale-prefixed and locale-suffixed routes.
 * Prefix format:  /:country/:locale/rest-of-path   (e.g. /ca/en-ca/products/smart-collar-v3b)
 * Suffix format:  /rest-of-path/:locale/:country    (e.g. /products/smart-collar-v3b/en-ca/ca)
 * Extracts the country and locale, sets them in context, and redirects to the plain path.
 */
export function LocaleRouteWrapper({ children }: { children: React.ReactNode }) {
  return (
    <Switch>
      {/* Match locale-prefixed routes - be specific to avoid matching regular paths */}
      <Route path={LOCALE_ROUTE_PATH} exact={false}>
        <LocaleExtractor>{children}</LocaleExtractor>
      </Route>
      {/* Regular routes (may contain locale suffix) */}
      <Route path="/">
        <LocaleSuffixHandler>{children}</LocaleSuffixHandler>
      </Route>
    </Switch>
  );
}

// Helper to redirect to path without locale prefix
const redirectToUnprefixedPath = (location: ReturnType<typeof useLocation>, history: ReturnType<typeof useHistory>) => {
  const parts = location.pathname.split('/');
  const pathWithoutLocale =
    '/' +
    parts
      .slice(3)
      .filter((p) => p)
      .join('/');
  const newPath = pathWithoutLocale + location.search + location.hash;
  history.replace(newPath);
};

function LocaleExtractor({ children }: { children: React.ReactNode }) {
  const params = useParams<{ country: string; locale: string }>();
  const { setCountry, setLanguage } = useLocale();
  const history = useHistory();
  const location = useLocation();

  useEffect(() => {
    const { country, locale } = params;

    // Validate country
    const validCountries = Object.values(FiCountry);
    const matchedCountry = validCountries.find((c) => c.toLowerCase() === country?.toLowerCase());

    // Validate locale
    const validLocales = Object.values(FiLanguage);
    const matchedLocale = validLocales.find((l) => l.toLowerCase() === locale?.toLowerCase());

    // If either is invalid, redirect without prefix
    if (!matchedCountry || !matchedLocale) {
      redirectToUnprefixedPath(location, history);
      return;
    }

    // Set country and language
    setCountry(matchedCountry, { chosen: true });
    setLanguage(matchedLocale);

    // Redirect to the path without locale prefix
    redirectToUnprefixedPath(location, history);
  }, [params, setCountry, setLanguage, history, location]);

  // Don't render children until after redirect
  return null;
}

/**
 * Detects locale/country as a URL suffix (e.g. /products/smart-collar-v3b/en-ca/ca).
 * If found, sets locale context and redirects to the URL without the suffix.
 * If not found, renders children as normal.
 */
function LocaleSuffixHandler({ children }: { children: React.ReactNode }) {
  const { setCountry, setLanguage } = useLocale();
  const history = useHistory();
  const location = useLocation();

  const parts = useMemo(() => location.pathname.split('/').filter(Boolean), [location.pathname]);

  const suffixMatch = useMemo(() => {
    if (parts.length < 3) return null; // Need at least one real path segment + two locale/country segments

    const secondToLast = parts[parts.length - 2]?.toLowerCase();
    const last = parts[parts.length - 1]?.toLowerCase();

    // Try both orderings: /locale/country and /country/locale
    const tryMatch = (localeSeg: string, countrySeg: string) => {
      if (!VALID_LOCALES_LOWER.includes(localeSeg) || !VALID_COUNTRIES_LOWER.includes(countrySeg)) return null;

      const matchedLocale = Object.values(FiLanguage).find((l) => l.toLowerCase() === localeSeg);
      const matchedCountry = Object.values(FiCountry).find((c) => c.toLowerCase() === countrySeg);

      if (!matchedLocale || !matchedCountry) return null;
      return { locale: matchedLocale, country: matchedCountry };
    };

    // Order 1: /path/locale/country  (e.g. /products/smart-collar-v3b/en-ca/ca)
    // Order 2: /path/country/locale  (e.g. /products/smart-collar-v3b/ca/fr-ca)
    return tryMatch(secondToLast, last) ?? tryMatch(last, secondToLast);
  }, [parts]);

  useEffect(() => {
    if (!suffixMatch) return;

    setCountry(suffixMatch.country, { chosen: true });
    setLanguage(suffixMatch.locale);

    // Redirect to path without the locale suffix
    const pathWithoutSuffix = '/' + parts.slice(0, -2).join('/');
    const newPath = pathWithoutSuffix + location.search + location.hash;
    history.replace(newPath);
  }, [suffixMatch, setCountry, setLanguage, history, location, parts]);

  // Don't render children until redirect completes when suffix is detected
  if (suffixMatch) return null;

  return <>{children}</>;
}
